import { type IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';
import {
  type VideoMemberEventParams,
  type VideoRoomAudienceCountEventParams,
  type VideoMemberEntity,
} from '@signalwire/core';
import { type AnyStateMachine, type StateValueMap } from 'xstate';

import type AnalyticServices from '../analyticServices';
import { type IDeviceInfoProvider } from '../Config';
import { type Platform, type Preference } from '../Constants';
import { type IMarketingDataProvider, type ILocationProvider, type ICredentialsProvider } from '../Container';
import DataServices, { PlaybacksHashMap} from '../dataServices/DataServices';
import type Episode from '../dataServices/Episode';
import type StandaloneChapter from '../dataServices/StandaloneChapter';
import type Station from '../dataServices/Station';
import { type ILogger } from '../logger';
import PersonalizationServices, { UnauthenticatedServices} from '../personalizationServices';
import { type IPlayerSettingsProvider } from '../personalizationServices/types';
import { type IPlayer, type TPlaybackRate, type IPlayerMetadata } from './players/types';
import { type IStreamerState, type ITime } from './streamers/types';
import { type findStreamer, type initialize, type startStreamer } from './utils';

export type IAudioServicesStateListenerParams = {
  audioServicesState: IAudioServicesState;
  streamerState?: IStreamerState<TPlayableObject>;
}

export type TAudioServicesListener = (params: IAudioServicesStateListenerParams) => void;

export type IAudioServicesSignalWireListenerParams = {
  type: string;
  data?: TSignalWireEventData;
}

export type TAudioServicesSignalWireListener = (
  params: IAudioServicesSignalWireListenerParams,
) => void;

export enum EContentType {
  ExclusiveStation = 'EXCLUSIVE_STATION',
  LiveStation = 'LIVE_STATION',
  Podcast = 'PODCAST',
  RewindNoReplay = 'LIVE_REWIND_NO_REPLAY',
  Rewind = 'LIVE_REWIND',
}

export type TPlayableObject = Episode | Station | StandaloneChapter;

// TODO: [CCS-1380] Rename IPersonalizationProvider
export type IPersonalizationProvider = Pick<
  PersonalizationServices,
  | 'getPlaybacks'
  | 'setPlayback'
  | 'setPlaybacks'
  | 'getQueue'
  | 'isInTopOfHistoryStack'
  | 'addToHistory'
  | 'callBrazeEvent'
>;

export type IUnauthenticatedProvider = Pick<
  UnauthenticatedServices,
  | 'setUnauthenticatedPlaybackTime'
  | 'getUnauthenticatedPlaybackTime'
  | 'getMaxFreeListeningTime'
  | 'dataStore'
  | 'unauthenticatedPlaybackTime'
  | 'isNewDay'
  | 'resetPlayedContentDaily'
>;

export type IAnalyticsProvider = Pick<
  AnalyticServices,
  | 'sendPlayerEvent'
  | 'platform'
  | 'sendEventToListener'
  | 'sendSleepTimerSetEvent'
  | 'sendPlaybackErrorEvent'
  | 'sendSongListenedEvent'
>;

// TODO: [CCS-487] Remove once DataServices is ported to TS
export type IDataProvider = Pick<
  DataServices,
  'getSegments' | 'getContentObject' | 'updateInteractionTime' | 'sessionId' | 'env' | 'stats'
>;

export enum ESleepTimerState {
  Idle = 'IDLE',
  EndOfEpisode = 'END_OF_EPISODE',
}

export enum ECollectionPlaybackMode {
  SingleItem = 'SINGLE_ITEM',
  Queue = 'QUEUE',
  Playlist = 'PLAYLIST',
}
export type IMachineContext = {
  platform: Platform;
  player: IPlayer;
  personalizationProvider: IPersonalizationProvider;
  unauthenticatedProvider?: IUnauthenticatedProvider;
  playerSettingsProvider: IPlayerSettingsProvider;
  dataProvider: IDataProvider;
  marketingDataProvider?: IMarketingDataProvider;
  locationProvider?: ILocationProvider;
  credentialsProvider?: ICredentialsProvider;
  signalWireEventListener?: TAudioServicesSignalWireListener;
  logger: ILogger;
  autoplay: boolean;
  playLive: boolean;
  analyticsProvider: IAnalyticsProvider;
  skipsCount: number;
  deviceInfoProvider?: IDeviceInfoProvider;
}
export type IAudioServicesMachineContext = {
  isMuted: boolean; // Separate from volume so that we can retain pre-muted volume when unmuting
  volume: number; // 0 to 1 inclusive
  rate: TPlaybackRate;
  sleepTimerState: number | ESleepTimerState; // Epoch time in milliseconds, 'Idle', or 'EndOfEpisode'
  activePlayableObject?: TPlayableObject; // undefined when machine is idle
  activeCollection: Array<TPlayableObject>; // Empty array for single-item playback mode
  analyticsProvider: IAnalyticsProvider;
  deviceInfoProvider?: IDeviceInfoProvider;
  playbacks: PlaybacksHashMap;
  startOffset?: number; // undefined when machine is idle
  collectionPlaybackMode: ECollectionPlaybackMode;
  currentStreamerMachine?: AnyStateMachine;
  playbackTimer: {
    elapsed: ITime;
    accumulator: ITime;
    lastStartedTime?: ITime;
  };
  featureFlags?: IFeatureFlags;
  useMp3RewindStreamer?: boolean;
} & IMachineContext

export type IAudioEventListenerContext = {
  isMuted: boolean; // Separate from volume so that we can retain pre-muted volume when unmuting
  volume: number; // 0 to 1 inclusive
  rate: TPlaybackRate;
  sleepTimerState: number | ESleepTimerState; // Epoch time in milliseconds, 'Idle', or 'EndOfEpisode'
  activePlayableObject?: TPlayableObject; // undefined when machine is idle
  playbacks: PlaybacksHashMap;
  activeCollection: Array<TPlayableObject>;
  collectionPlaybackMode: ECollectionPlaybackMode;
}

export type IAudioServicesState = {
  value: StateValueMap;
  context: IAudioEventListenerContext;
}

export type IFailure = {
  type: 'REPORT_FAILURE';
  errorMessage: string;
  error?: {
    type: string;
    raw?: unknown;
  };
}

export type TPlayerReportEvent =
  | { type: 'REPORT_TIME_UPDATE'; time: number; duration?: number }
  | { type: 'REPORT_METADATA'; metadata: IPlayerMetadata }
  | { type: 'REPORT_ENDED'; shouldDestroy?: boolean }
  | { type: 'REPORT_LOADING' }
  | { type: 'REPORT_LOADED' }
  | { type: 'DESTROY' }
  | IFailure
  | {
      type: 'REPORT_PLAYBACK_FAILED';
      errorMessage: string;
      error?: {
        type: string;
        raw?: unknown;
      };
    }
  | { type: 'REPORT_PLAYING' }
  | { type: 'REPORT_PAUSED' }
  | { type: 'REPORT_AUTOPLAY_POLICY' }
  | {
      type: 'REPORT_CONTINUE_PLAYING';
      playlistNextStartTime: number;
      playLive?: boolean;
    };

export type TSignalWireEventData =
  | VideoMemberEventParams
  | VideoRoomAudienceCountEventParams
  | { members: Array<VideoMemberEntity> };

export type TSignalWireReportEvent =
  | { type: 'REPORT_ROOM_JOINED'; data: TSignalWireEventData }
  | { type: 'REPORT_ROOM_LEFT' }
  | { type: 'REPORT_ROOM_UPDATED'; data: TSignalWireEventData }
  | { type: 'REPORT_MEMBER_TALKING'; data: TSignalWireEventData }
  | { type: 'REPORT_MEMBER_UPDATED'; data: TSignalWireEventData }
  | { type: 'REPORT_MEMBER_LIST_UPDATED'; data: TSignalWireEventData };

export type TStreamerMachineEvent =
  | { type: 'PAUSE'; shouldDestroy?: boolean }
  | { type: 'STOP' }
  | { type: 'PLAY'; autoplay: boolean }

  | { type: 'SKIP' }
  | { type: 'GO_TO_LIVE' }
  | { type: 'SEEK_TIME'; time: number }
  | { type: 'SEND_PREFERENCE'; preference: Preference }
  | { type: 'DESTROY' }
  | { type: 'SET_MIC_MUTED'; isMuted: boolean }
  // [OOP-734] Manual retry from a CHAPTERS_REFRESH_FAILED state. Dispatched
  // by the UI when the listener taps "refresh chapters" / similar
  // affordance. MP3 Rewind only — other streamers ignore it.
  | { type: 'REFRESH_CHAPTERS' }
  | {
      type: 'SET_SIGNAL_WIRE_EVENT_LISTENER';
      signalWireEventListener: TAudioServicesSignalWireListener;
    }
  | TPlayerReportEvent
  | TSignalWireReportEvent;

export type TAudioServicesMachineEvent =
  | {
      type: 'LOAD_OBJECT';
      data: TPlayableObject;
      autoplay: boolean;
      offset?: number;
      playLive: boolean;
    }
  | { type: 'UPDATE_COLLECTION'; data: Array<TPlayableObject> }
  | { type: 'UPDATE_PLAYBACKS'; data: PlaybacksHashMap }
  | { type: 'SET_SLEEP_TIMER'; sleepTimer: number | ESleepTimerState }
  | { type: 'CANCEL_SLEEP_TIMER' }
  | { type: 'SET_MUTED'; isMuted: boolean }
  | { type: 'SET_VOLUME'; volume: number }
  | { type: 'SET_PLAYBACK_RATE'; rate: TPlaybackRate }
  | { type: 'SET_COLLECTION_PLAYBACK_MODE'; mode: ECollectionPlaybackMode }
  | { type: 'INCREMENT_PLAYBACK_RATE' }
  | { type: 'NEXT'; shouldSkipCompletedItems?: boolean }
  | { type: 'PREVIOUS'; shouldSkipCompletedItems?: boolean }
  | { type: 'SCRUB' }
  | TStreamerMachineEvent;

export type TAudioServicesMachineServices = {
  initialize: {
    data: Awaited<ReturnType<typeof initialize>>;
  };
  findStreamer: {
    data: Awaited<ReturnType<typeof findStreamer>>;
  };
  startStreamer: {
    data: ReturnType<typeof startStreamer>;
  };
};
